"use client";

/* eslint-disable react/prop-types */

import { FC, memo, type ReactNode } from "react";
import type { StockListingUILabels } from "../../../interfaces/StockListingTableAEM.interface";
import { useStockListingFilters } from "../context/StockListingFiltersContext";
import StockFiltersMobileDrawer from "./StockFiltersMobileDrawer";
import StockSortDropdown, { SortOption } from "./StockSortDropdown";
import StockActiveFilterChips from "./StockActiveFilterChips";

export interface StockHeaderBarProps {
  /** Header text to display - from AEM, can be null */
  headerText: string | null;
  /** Current sort value */
  sortValue: string;
  /** Available sort options */
  sortOptions: SortOption[];
  /** Variant: "desktop" or "mobile" */
  variant?: "desktop" | "mobile";
  /** If false, hides Sort section in mobile Filters drawer */
  showMobileSortInDrawer?: boolean;
  /** When true, renders the filters drawer on mobile instead of the sort dropdown */
  showFiltersDrawer?: boolean;
  /** When true, hides the sort dropdown entirely */
  hideSort?: boolean;
  /**
   * Server-rendered text node passed from a React Server Component.
   * When provided, it is used as the LCP text element instead of creating
   * a new element client-side, so it paints from the initial HTML stream.
   */
  headerTextSlot?: ReactNode;
  /** AEM UI labels */
  uiLabels?: StockListingUILabels;
}

type StockHeaderBarMobileProps = {
  headerText: string | null;
  sortValue: string;
  sortOptions: SortOption[];
  showMobileSortInDrawer: boolean;
  showFiltersDrawer: boolean;
  headerTextSlot?: ReactNode;
  uiLabels: StockListingUILabels;
};

/** Mobile-only: uses filter context for applied chips in the header bar. */
const StockHeaderBarMobile: FC<StockHeaderBarMobileProps> = memo(
  ({
    headerText,
    sortValue,
    sortOptions,
    showMobileSortInDrawer,
    showFiltersDrawer,
    headerTextSlot,
    uiLabels,
  }) => {
    const { activeFilters } = useStockListingFilters();
    const hasActiveFilters =
      (activeFilters?.index?.length ?? 0) > 0 ||
      (activeFilters?.sector?.length ?? 0) > 0 ||
      (activeFilters?.mktcap?.length ?? 0) > 0 ||
      (activeFilters?.alphabetic?.length ?? 0) > 0;

    return (
      <div className="box-border flex items-stretch justify-between border border-[#d2d2d2] bg-white leading-[19.2px] md:hidden">
        {hasActiveFilters ? (
          <div className="p-4 md:px-0 w-79 md:w-unset">
            <StockActiveFilterChips uiLabels={uiLabels} mode="mobileHeader" />
          </div>
        ) : (
          headerTextSlot ?? (headerText && (
            <span className="box-border m-0 p-4 md:px-0 text-base font-medium leading-normal text-[rgb(34,47,63)]">
              {headerText}
            </span>
          ))
        )}
        {showFiltersDrawer ? (
          <StockFiltersMobileDrawer
            sortValue={sortValue}
            sortOptions={sortOptions}
            showSortSection={showMobileSortInDrawer}
            uiLabels={uiLabels}
          />
        ) : (
          <StockSortDropdown sortValue={sortValue} sortOptions={sortOptions} variant="mobile" uiLabels={uiLabels} />
        )}
      </div>
    );
  },
);

StockHeaderBarMobile.displayName = "StockHeaderBarMobile";

/**
 * Header bar component that displays header text and sort dropdown.
 * Used for both desktop and mobile views.
 * If headerText is null, only sort dropdown is rendered.
 */
const StockHeaderBar: FC<StockHeaderBarProps> = memo(
  ({
    headerText,
    sortValue,
    sortOptions,
    variant = "desktop",
    showMobileSortInDrawer = true,
    showFiltersDrawer = false,
    hideSort = false,
    headerTextSlot,
    uiLabels = {},
  }) => {
    if (variant === "mobile") {
      return (
        <StockHeaderBarMobile
          headerText={headerText}
          sortValue={sortValue}
          sortOptions={sortOptions}
          showMobileSortInDrawer={showMobileSortInDrawer}
          showFiltersDrawer={showFiltersDrawer}
          headerTextSlot={headerTextSlot}
          uiLabels={uiLabels}
        />
      );
    }

    return (
      <div className="hidden md:flex box-border w-full max-h-[50px] items-center justify-between border-b-3 border-b-[rgba(0,0,0,0.08)] whitespace-nowrap py-[10px] pl-6 pr-6 mt-1 leading-[19.2px]">
        {headerTextSlot ?? (headerText && (
          <p className="box-border flex h-[28px] w-[173.922px] items-center text-sm font-normal leading-[19.2px] text-[rgb(34,47,63)] whitespace-nowrap [unicode-bidi:isolate]">
            {headerText}
          </p>
        ))}
        {!hideSort && <StockSortDropdown sortValue={sortValue} sortOptions={sortOptions} variant="desktop" uiLabels={uiLabels} />}
      </div>
    );
  },
);

StockHeaderBar.displayName = "StockHeaderBar";

export default StockHeaderBar;
